/**
 * Print-accurate screenplay pagination.
 * Replicates backend/export_service.py's PDF layout math exactly (12pt Courier,
 * US Letter, 1.5" left / 1" right/top/bottom margins) so the page count shown
 * on screen always matches the exported PDF — on any device or window size.
 * Title page is unnumbered and never counted (industry standard).
 *
 * Widow/orphan (keep-together) rules, Final Draft style:
 *  - scene headings / shots never end a page (kept with what follows)
 *  - character cues keep their parenthetical(s) + first dialogue block
 *  - parentheticals keep the dialogue they modify
 *  - transitions never start a page (kept with the block before them)
 */
const LINE = 12;            // 12pt = one Courier line
const TOP_Y = 792 - 72;     // PAGE_H - TOP_MARGIN (points)
const BOTTOM = 72;          // BOTTOM_MARGIN (points)

// Mirrors _wrap_text() in export_service.py — returns wrapped line count.
function wrapCount(text, width) {
  if (!text) return 1;
  const words = text.split(" ");
  let count = 0;
  let cur = "";
  for (const w of words) {
    const candidate = cur ? `${cur} ${w}`.trim() : w;
    if (candidate.length <= width) {
      cur = candidate;
    } else {
      if (cur) count += 1;
      cur = w;
    }
  }
  if (cur) count += 1;
  return Math.max(1, count);
}

// [ensureLines, consumeLines] for a non-empty element — mirrors _block_lines().
function blockLines(type, text) {
  if (type === "scene_heading") return [2, 2];
  if (type === "transition") return [1, 1.5];
  if (type === "character" || type === "parenthetical") return [1, 1];
  if (type === "dialogue") {
    const n = wrapCount(text, 35);
    return [n, n + 0.25];
  }
  if (type === "shot" || type === "subheader") return [1, 1.25];
  const n = wrapCount(text, 60); // action / default
  return [n, n + 0.5];
}

/**
 * Paginate elements with the exact PDF exporter algorithm.
 * Returns { total, pages, hasContent } where pages[i] is the 1-based page
 * the i-th element prints on.
 */
export function paginatePrint(elements) {
  const els = (elements || []).map((el) => ({
    type: el?.type || "action",
    text: (el?.text || "").trim(),
  }));
  const n = els.length;

  const nextNonEmpty = (k) => {
    while (k < n && !els[k].text) k += 1;
    return k;
  };

  // Mirrors _group_last(): last index that must share a page with element i.
  const groupLast = (i) => {
    const t = els[i].type;
    let last = i;
    if (t === "scene_heading" || t === "shot" || t === "subheader") {
      const j = nextNonEmpty(i + 1);
      if (j < n) {
        last = j;
        const tj = els[j].type;
        if (tj === "character") {
          let k = nextNonEmpty(j + 1);
          while (k < n && els[k].type === "parenthetical") {
            last = k;
            k = nextNonEmpty(k + 1);
          }
          if (k < n && els[k].type === "dialogue") last = k;
        } else if (tj === "parenthetical") {
          const k = nextNonEmpty(j + 1);
          if (k < n && els[k].type === "dialogue") last = k;
        }
      }
    } else if (t === "character") {
      let k = nextNonEmpty(i + 1);
      while (k < n && els[k].type === "parenthetical") {
        last = k;
        k = nextNonEmpty(k + 1);
      }
      if (k < n && els[k].type === "dialogue") last = k;
    } else if (t === "parenthetical") {
      const k = nextNonEmpty(i + 1);
      if (k < n && els[k].type === "dialogue") last = k;
    }
    if (els[last].type !== "transition") {
      const m = nextNonEmpty(last + 1);
      if (m < n && els[m].type === "transition") last = m;
    }
    return last;
  };

  const groupLines = (i, last) => {
    let total = 0;
    for (let k = i; k <= last; k += 1) {
      if (!els[k].text) { total += 1; continue; }
      const [ens, cons] = blockLines(els[k].type, els[k].text);
      total += k === last ? ens : cons;
    }
    return total;
  };

  let y = TOP_Y;
  let page = 1;
  const pages = new Array(n).fill(1);
  let hasContent = false;

  const newPage = () => { page += 1; y = TOP_Y; };
  const ensure = (lines) => { if (y - lines * LINE < BOTTOM) newPage(); };

  for (let i = 0; i < n; i += 1) {
    const { type, text } = els[i];
    if (!text) {
      pages[i] = page;
      y -= LINE;
      continue;
    }
    hasContent = true;
    // One keep-together check per element (standalone element == old behavior).
    ensure(groupLines(i, groupLast(i)));
    pages[i] = page;
    if (type === "scene_heading") {
      y -= LINE * 2; // 0.5 lead-in + heading line + 0.5 trailing
    } else if (type === "transition") {
      y -= LINE * 1.5;
    } else if (type === "character" || type === "parenthetical") {
      y -= LINE;
    } else if (type === "dialogue") {
      y -= LINE * wrapCount(text, 35) + LINE * 0.25;
    } else if (type === "shot" || type === "subheader") {
      y -= LINE * 1.25;
    } else { // action / default
      y -= LINE * wrapCount(text, 60) + LINE * 0.5;
    }
  }
  return { total: page, pages, hasContent };
}

export function estimatePageCount(elements) {
  const { total, hasContent } = paginatePrint(elements);
  return hasContent ? total : 0;
}

export function pageCountLabel(elements) {
  const n = estimatePageCount(elements);
  if (n === 0) return "Empty";
  return `${n} page${n === 1 ? "" : "s"}`;
}
